import { Link } from "react-router-dom";
import { Heart, Camera, MapPin } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useAppData } from "../context/AppDataContext";
import { fileUrl } from "../lib/api";
import { formatPrice, relativeDate } from "../lib/format";

const PROPERTY = ["alojamientos", "inmobiliaria", "hoteles"];

function propertyLine(a = {}, lang) {
  const en = lang === "en";
  const parts = [];
  if (a.rooms) parts.push(`${a.rooms} ${en ? "bd" : "hab"}`);
  if (a.bathrooms) parts.push(`${a.bathrooms} ${en ? (a.bathrooms > 1 ? "baths" : "bath") : a.bathrooms > 1 ? "baños" : "baño"}`);
  if (a.area) parts.push(`${a.area} m²`);
  return parts.join(" · ");
}

export function ListingSkeleton({ portrait }) {
  return (
    <div className="animate-pulse" data-testid="listing-skeleton">
      <div className={`${portrait ? "aspect-[3/4]" : "aspect-[4/3]"} rounded-[14px] bg-slate-100`} />
      <div className="mt-2 h-4 w-1/3 rounded bg-slate-100" />
      <div className="mt-1.5 h-3.5 w-11/12 rounded bg-slate-100" />
      <div className="mt-1.5 h-3 w-1/2 rounded bg-slate-100" />
    </div>
  );
}

export default function ListingCard({ listing, index = 0 }) {
  const { t, lang } = useLang();
  const { favorites, toggleFavorite, zoneName } = useAppData();
  const fav = favorites.has(listing.id);
  const adult = listing.category === "contactos";
  const features = PROPERTY.includes(listing.category) ? propertyLine(listing.attributes, lang) : "";
  const count = listing.photos?.length || 0;
  const onFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(listing.id);
  };
  return (
    <Link
      to={`/anuncio/${listing.id}`}
      className="group block min-w-0 fade-up"
      style={{ animationDelay: `${Math.min(index % 20, 12) * 35}ms` }}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className={`relative overflow-hidden rounded-[14px] bg-mist shadow-[0_2px_12px_rgba(13,42,107,0.08)] ${adult ? "aspect-[3/4]" : "aspect-[4/3]"}`}>
        <img src={fileUrl(listing.photos[0])} alt={listing.title} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <button onClick={onFav} aria-label="favorite" data-testid={`fav-btn-${listing.id}`}
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md transition-transform duration-200 active:scale-75">
          <Heart className={`h-4 w-4 text-alert ${fav ? "fill-alert" : ""}`} strokeWidth={2.2} />
        </button>
        {count > 1 && (
          <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-navy/70 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur" data-testid="listing-card-photo-count">
            <Camera className="h-3 w-3" /> {count}
          </span>
        )}
        {adult && <span className="absolute left-2 top-2 rounded-full bg-alert px-1.5 py-0.5 text-[10px] font-extrabold text-white">+18</span>}
      </div>
      <div className="px-0.5 pt-2">
        {!adult && (
          <p className="truncate text-[16px] font-extrabold leading-tight text-[#0D47A1]" data-testid="listing-card-price">
            {formatPrice(listing, t, lang)}
          </p>
        )}
        <h3 className={`line-clamp-2 leading-snug text-navy ${adult ? "text-[15px] font-extrabold" : "mt-0.5 text-[14px] font-semibold"}`} data-testid="listing-card-title">
          {listing.title}
        </h3>
        {features && <p className="mt-0.5 truncate text-[12px] font-medium text-slate-600" data-testid="listing-card-features">{features}</p>}
        <p className="mt-1 flex items-center gap-1 truncate text-[12px] text-slate-500" data-testid="listing-card-meta">
          <MapPin className="h-3 w-3 shrink-0 text-alert" />
          <span className="truncate">{zoneName(listing.zone)} · {relativeDate(listing.created_at, lang)}</span>
        </p>
      </div>
    </Link>
  );
}
